import React, { useEffect, useState } from 'react';
import { Camera, Check, LocateFixed, Save, Settings as SettingsIcon, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPatch, apiPost } from '../../lib/api';
import { uploadFile } from '../../lib/upload';
import { useAuth } from '../../context/AuthContext';

const inputCls = 'w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rozops-blue/20 focus:border-rozops-blue';

export default function Settings() {
  const { refreshProfile } = useAuth();
  const [profile, setProfile] = useState(null);
  const [business, setBusiness] = useState(null);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [upgradingId, setUpgradingId] = useState(null);

  const load = () => apiGet('/merchant/settings').then((res) => {
    const data = res.data || {};
    setProfile(data.profile || null);
    setBusiness(data.business || null);
    setPlans(data.plans || []);
    setForm({
      owner_name: data.profile?.full_name || '',
      mobile: data.business?.phone || data.profile?.phone || '',
      email: data.business?.email || data.profile?.email || '',
      business_name: data.business?.business_name || '',
      address: data.business?.address || '',
      city: data.business?.city || '',
      state: data.business?.state || '',
      pincode: data.business?.pincode || '',
      latitude: data.business?.latitude || '',
      longitude: data.business?.longitude || '',
    });
  }).catch((err) => toast.error(err.message));

  useEffect(() => { load(); }, []);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        latitude: form.latitude === '' ? null : Number(form.latitude),
        longitude: form.longitude === '' ? null : Number(form.longitude),
      };
      const res = await apiPatch('/merchant/settings', payload);
      setProfile(res.data?.profile || profile);
      setBusiness(res.data?.business || business);
      await refreshProfile();
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const useGps = () => {
    if (!navigator.geolocation) {
      toast.error('GPS location is not supported in this browser');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm({
          ...form,
          latitude: pos.coords.latitude.toFixed(7),
          longitude: pos.coords.longitude.toFixed(7),
        });
        toast.success('GPS location captured');
      },
      (err) => toast.error(err.message || 'Could not capture GPS location'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile('/merchant/settings/logo', file);
      setBusiness(res.data?.business || { ...business, logo_url: res.data?.logo_url });
      await refreshProfile();
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const upgrade = async (plan) => {
    setUpgradingId(plan.id);
    try {
      const price = Number(plan.price || 0);
      if (price <= 0) {
        const res = await apiPost('/merchant/settings/upgrade', { plan_id: plan.id });
        setBusiness(res.data?.business || business);
        await refreshProfile();
        toast.success(`Plan changed to ${plan.name}`);
        setUpgradingId(null);
        return;
      }
      if (!window.Razorpay) {
        toast.error('Razorpay checkout is not loaded. Please refresh and try again.');
        setUpgradingId(null);
        return;
      }

      const res = await apiPost('/merchant/settings/plan-order', { plan_id: plan.id });
      const { order, key_id, amount, business: checkoutBusiness } = res.data;
      const options = {
        key: key_id,
        amount,
        currency: 'INR',
        name: 'RozOps',
        description: `${plan.name} subscription`,
        order_id: order.id,
        prefill: {
          name: checkoutBusiness?.business_name || business?.business_name || '',
          contact: checkoutBusiness?.phone || business?.phone || profile?.phone || '',
          email: checkoutBusiness?.email || business?.email || profile?.email || '',
        },
        theme: { color: '#5B21F4' },
        modal: {
          ondismiss: () => {
            setUpgradingId(null);
            toast.info('Plan payment cancelled');
          },
        },
        handler: async (resp) => {
          try {
            const verified = await apiPost('/merchant/settings/plan-verify', {
              plan_id: plan.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setBusiness(verified.data?.business || business);
            await refreshProfile();
            toast.success(`Plan upgraded to ${plan.name}`);
          } catch (err) {
            toast.error(err.message);
          } finally {
            setUpgradingId(null);
          }
        },
      };
      const rz = new window.Razorpay(options);
      rz.open();
    } catch (err) {
      toast.error(err.message);
      setUpgradingId(null);
    }
  };

  const currentPlan = (business?.subscription_plan || 'free').toLowerCase();

  return (
    <div className="space-y-6" data-testid="merchant-settings">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-rozops-blue-light text-rozops-blue grid place-items-center"><SettingsIcon className="w-5 h-5" /></div>
        <h1 className="font-heading font-extrabold text-3xl text-rozops-navy tracking-tight">Settings</h1>
      </div>

      <form onSubmit={save} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 space-y-4">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-2xl bg-rozops-blue-light text-rozops-blue overflow-hidden grid place-items-center">
            {business?.logo_url ? <img src={business.logo_url} alt="Merchant logo" className="w-full h-full object-cover" /> : <Camera className="w-9 h-9" />}
          </div>
          <div>
            <div className="text-sm font-semibold text-rozops-text mb-2">Merchant logo</div>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border-2 border-rozops-blue-light px-4 py-2.5 text-sm font-semibold text-rozops-blue hover:border-rozops-blue">
              <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload logo'}
              <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="merchant-logo-upload" />
            </label>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Merchant name" value={form.owner_name || ''} onChange={update('owner_name')} testId="settings-owner-name" />
          <Field label="Business name" value={form.business_name || ''} onChange={update('business_name')} testId="settings-business-name" />
          <Field label="Mobile" value={form.mobile || ''} onChange={update('mobile')} testId="settings-mobile" />
          <Field label="Email ID" type="email" value={form.email || ''} onChange={update('email')} testId="settings-email" />
        </div>

        <div>
          <label className="block text-sm font-semibold text-rozops-text mb-1.5">Address</label>
          <textarea rows={3} className={inputCls} value={form.address || ''} onChange={update('address')} data-testid="settings-address" />
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <Field label="City" value={form.city || ''} onChange={update('city')} />
          <Field label="State" value={form.state || ''} onChange={update('state')} />
          <Field label="Pincode" value={form.pincode || ''} onChange={update('pincode')} />
        </div>

        <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <Field label="GPS latitude" type="number" step="0.0000001" value={form.latitude || ''} onChange={update('latitude')} testId="settings-latitude" />
          <Field label="GPS longitude" type="number" step="0.0000001" value={form.longitude || ''} onChange={update('longitude')} testId="settings-longitude" />
          <button type="button" onClick={useGps} className="rounded-xl border-2 border-rozops-blue-light px-4 py-2.5 text-sm font-semibold text-rozops-blue inline-flex items-center justify-center gap-2">
            <LocateFixed className="w-4 h-4" /> Use GPS
          </button>
        </div>

        <button disabled={saving} className="bg-rozops-blue text-white rounded-xl px-4 py-2.5 font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="settings-save">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save settings'}
        </button>
      </form>

      <div className="space-y-3">
        <h2 className="font-heading font-bold text-xl text-rozops-navy">Subscription plan</h2>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
          {plans.map((plan) => {
            const active = currentPlan === (plan.name || '').toLowerCase();
            return (
              <div key={plan.id} className={`bg-white rounded-2xl border shadow-soft p-5 ${active ? 'border-rozops-blue' : 'border-gray-100'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-heading font-bold text-lg text-rozops-navy">{plan.name}</div>
                    <div className="mt-1 text-2xl font-heading font-extrabold text-rozops-blue">₹{Number(plan.price || 0).toFixed(0)}<span className="text-sm text-rozops-text-muted">/{plan.billing_cycle || 'monthly'}</span></div>
                  </div>
                  {active && <span className="rounded-full bg-rozops-green-light px-2.5 py-1 text-xs font-bold text-rozops-green inline-flex items-center gap-1"><Check className="w-3 h-3" /> Current</span>}
                </div>
                <div className="mt-3 text-xs text-rozops-text-muted">{plan.customer_limit ? `Up to ${plan.customer_limit} customers` : 'Unlimited customers'}</div>
                <div className="mt-4 space-y-2">
                  {(plan.features || []).slice(0, 4).map((feature) => <div key={feature} className="text-sm text-rozops-text">{feature}</div>)}
                </div>
                <button type="button" disabled={active || upgradingId === plan.id} onClick={() => upgrade(plan)} className="mt-5 w-full rounded-xl px-4 py-2.5 text-sm font-semibold bg-rozops-blue text-white disabled:bg-gray-100 disabled:text-rozops-text-muted" data-testid={`upgrade-${plan.name.toLowerCase()}`}>
                  {active ? 'Current plan' : upgradingId === plan.id ? 'Opening gateway...' : Number(plan.price || 0) > 0 ? 'Pay & upgrade' : 'Switch plan'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-rozops-text mb-1.5">{label}</label>
      <input className={inputCls} data-testid={testId} {...props} />
    </div>
  );
}
